import React, { useRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAtom, useAtomValue } from 'jotai';
import { useSignIn } from '@clerk/nextjs';
import { useRouter } from 'next/router';
import Bugsnag from '@bugsnag/js';
import {
  authIdentifierAtom,
  authModeAtom,
  authSignInIDAtom,
  authStepAtom,
  authRedirectToAtom,
  authPasswordShowAtom,
  authIdentifierTypeAtom,
} from 'auth/atoms';
import { failedLoginMessageAtom, provisioningErrorHandledAtom } from 'auth/userStore';
import Button from 'components/ui/Button/Button';
import { InputWrapper } from 'components/auth_flow/SharedComponents';
import styled from 'styled-components';
import { FlexJustifyCenter, FlexRowReverse } from 'components/primitives/flex';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import useRudderStackTrack from 'hooks/useRudderStackTrack'; // Import the hook
import { LoginType } from 'auth/constants';
import useActivateSession from 'hooks/useActivateSession';
import EmailPasswordInput from './EmailPasswordInput';
import { useUserDataMutation } from '../hooks/authFlowHooks';
import useAuthComplete from '../hooks/useAuthComplete';

type FormValues = {
  email: string;
  password?: string;
};

const EmailIdentifierStep = () => {
  const { signIn, isLoaded } = useSignIn();
  const router = useRouter();
  const [, setStep] = useAtom(authStepAtom);
  const [, setMode] = useAtom(authModeAtom);
  const [, setIdentifier] = useAtom(authIdentifierAtom);
  const [, setSignInID] = useAtom(authSignInIDAtom);
  const [, setProvisioningErrorHandled] = useAtom(provisioningErrorHandledAtom);
  const [, setFailedLoginMessage] = useAtom(failedLoginMessageAtom);
  const [, setIdentifierType] = useAtom(authIdentifierTypeAtom);
  const [showPasswordInput, setShowPasswordInput] = useAtom(authPasswordShowAtom);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const isEmailInputInteractedRef = useRef<boolean>(false);
  const { enqueueSnackbar } = useLazySnackbar();
  const redirectTo = useAtomValue(authRedirectToAtom);
  const rudderTrack = useRudderStackTrack();
  const loginFormTracked = useRef(false);
  const userDataMutation = useUserDataMutation();
  const { runAnalytics } = useAuthComplete();
  const activateSession = useActivateSession();

  // Form setup with custom error handling
  const methods = useForm<FormValues>({
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  const { handleSubmit, getValues, reset } = methods;

  useEffect(() => {
    setShowPasswordInput(false);
    setStep('identifier');
    setMode('signin');
    setIdentifier('');
    setIdentifierType('email');
    reset();
  }, []);

  // Track when password field is shown
  useEffect(() => {
    // Only track if the password input is now visible AND we haven't tracked it yet
    // The ref allows us to track the component across renders without re-triggering
    if (showPasswordInput && !loginFormTracked.current) {
      rudderTrack('show_form', {
        component_type: 'form',
        object: 'log_in_form', // Specific event for login state
      });
      loginFormTracked.current = true; // Mark as tracked for this component instance
    }
    // Dependency array: run this effect when showPasswordInput changes
    // Include rudderTrack in case its identity changes
  }, [showPasswordInput, rudderTrack]);

  // Central error handler
  const handleError = (message: string) => {
    enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: 5000,
    });
  };

  const handleEmailBlur = async (email: string) => {
    if (!email) return;

    setIdentifier(email);

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i.test(email)) {
      handleError('Please enter a valid email address');
      return;
    }

    try {
      // Attempt to create a sign‑in with only the email
      await signIn.create({ identifier: email });
      setShowPasswordInput(true);
      // Focus the password field after it becomes visible
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 0);
    } catch (e: any) {
      const errorCode = e?.errors?.[0]?.code;
      if (errorCode === 'form_password_blank') {
        // Account exists but requires a password
        setShowPasswordInput(true);
        setTimeout(() => {
          passwordInputRef.current?.focus();
        }, 0);
      } else if (errorCode === 'form_identifier_not_found') {
        // Email not found in system: switch to the signup flow
        setIdentifier(email);
        setMode('signup');
        setStep('signupDetails');
      } else if (errorCode === 'session_exists') {
        enqueueSnackbar("You're already signed in", {
          variant: 'success',
          autoHideDuration: 5000,
        });
        await activateSession({ redirectTo });
      } else {
        console.error(e);
        handleError(e?.errors?.[0]?.message || 'An error occurred');
      }
    }
  };

  const handleResetPasswordClick = async () => {
    rudderTrack('click', {
      component_type: 'link',
      object: 'forgot_your_password',
      context: 'log_in_form',
    });

    const email = getValues('email');
    router.push(`/a/auth/forgot-password?email=${email}`);
  };

  const onSubmit = async (values: FormValues) => {
    rudderTrack('click', {
      component_type: 'button',
      object: showPasswordInput ? 'sign_in' : 'continue_with_email',
      context: showPasswordInput ? 'log_in_form' : 'log_in_or_sign_up_form',
    });

    const { email, password } = values;
    setIdentifier(email);

    // Validate email format
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i.test(email)) {
      handleError('Please enter a valid email address');
      return;
    }

    // If the password field isn't shown yet, trigger the email blur logic.
    if (!showPasswordInput) {
      await handleEmailBlur(email);
      return;
    }

    // Validate password is provided
    if (!password || password.trim() === '') {
      handleError('Please enter your password');
      return;
    }

    // Otherwise, we're in the "password" phase—attempt sign in with email and password.
    try {
      const signInAttempt = await signIn.create({
        identifier: email,
        password,
        strategy: 'password',
      });
      setSignInID(signInAttempt.id);
      if (signInAttempt.status === 'complete') {
        await activateSession({
          session: signInAttempt.createdSessionId,
          redirectTo,
          onAfterSuccess: async () => {
            setStep('identifier');
            setProvisioningErrorHandled(false); // Reset provisioning error state for login attempt
            setFailedLoginMessage('');
            const userData = await userDataMutation.mutateAsync();
            runAnalytics(userData, LoginType.EMAIL_LOGIN);
            rudderTrack('login_success', {
              object: 'log_in_form',
            });
          },
        });
      }
    } catch (e: any) {
      const errorCode = e?.errors?.[0]?.code;
      if (errorCode === 'form_password_incorrect') {
        handleError(e.errors[0].message || 'Password is incorrect');
      } else if (errorCode === 'form_identifier_not_found') {
        setMode('signup');
        setStep('signupDetails');
      } else if (errorCode === 'user_banned') {
        console.error(e);
        handleError(
          'This account has been disabled. If you believe this has happened in error, please contact support.'
        );
      } else if (errorCode === 'session_exists') {
        enqueueSnackbar("You're already signed in", {
          variant: 'success',
          autoHideDuration: 5000,
        });
        await activateSession({ redirectTo });
      } else {
        console.error('error', e?.errors?.[0]?.longMessage);
        Bugsnag.notify(e);
        handleError(e?.errors?.[0]?.message || 'Something went wrong. Please try again later.');
      }
    }
  };

  // Handle validation errors from form
  const onError = (errors: any) => {
    // Get the first error message
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const firstField = errorFields[0];
      const message = errors[firstField].message || `Please check your ${firstField}`;
      if (message === 'Please enter password' && !showPasswordInput) {
        return;
      }
      handleError(message);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isEmailInputInteractedRef.current) {
      rudderTrack('interact_with_form', {
        component_type: 'form_field',
        object: 'email_entry_form_field',
        context: 'log_in_or_sign_up_form',
      });
      isEmailInputInteractedRef.current = true;
    }

    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission on Enter key
      if (showPasswordInput) {
        handleSubmit(onSubmit, onError)();
      } else {
        handleEmailBlur(getValues('email'));
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <InputWrapper>
          <EmailPasswordInput
            onKeyDown={handleKeyDown}
            hidePassword={!showPasswordInput}
            onEmailBlur={async (value: string) => {
              if (!isEmailInputInteractedRef.current) {
                rudderTrack('interact_with_form', {
                  component_type: 'form_field',
                  object: 'email_entry_form_field',
                  context: 'log_in_or_sign_up_form',
                });
                isEmailInputInteractedRef.current = true;
              }
              // On email blur, run the account check
              if (value) {
                await handleEmailBlur(value);
              }
            }}
            passwordInputRef={passwordInputRef}
          />
          <FlexRowReverse>
            <ForgotPasswordButton type="button" onClick={handleResetPasswordClick}>
              Forgot your password?
            </ForgotPasswordButton>
          </FlexRowReverse>
        </InputWrapper>
        <Button
          type="submit"
          text={showPasswordInput ? 'Sign In' : 'Continue with email'}
          fullWidth
          disabled={!isLoaded}
          data-testid="auth-email-continue-button"
        />
        <FlexJustifyCenter style={{ marginTop: '20px' }}>
          <div id="clerk-captcha" data-cl-theme="light" data-cl-size="flexible" />
        </FlexJustifyCenter>
      </form>
    </FormProvider>
  );
};

const ForgotPasswordButton = styled.button`
  font-weight: 500;
  font-size: 14px;
  color: #0751c2;
`;

export default EmailIdentifierStep;
